// frontend/src/components/Admin/EnrollmentModal.tsx
// MP-STAB-04 — Modal de Device Enrollment: genera un Enrollment Link de alta entropía
// y ofrece acciones de entrega (WhatsApp, Email, Copiar enlace).
// El código de pairing es un DETALLE INTERNO — nunca se muestra en la UX normal.
// El link (ENR-xxx, 128+ bits) ES la credencial: el destinatario lo abre en el
// dispositivo a registrar y el backend consume internamente el Contrato de Registro.
import React, { useEffect, useState } from 'react';
import * as api from '../../services/api';

interface EnrollmentModalProps {
  token: string;
  adminId: string;
  adminName: string;
  onClose: () => void;
}

export default function EnrollmentModal({ token, adminId, adminName, onClose }: EnrollmentModalProps) {
  const [enrollment, setEnrollment] = useState<any>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const inviteUrl = enrollment ? `${window.location.origin}/enroll/${enrollment.id}` : '';

  // Crear el enrollment al abrir el modal (el pairing se crea internamente en el backend)
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await api.createEnrollment(adminId, '', token);
        if (data && data.enrollment) {
          setEnrollment(data.enrollment);
          setExpiresAt(data.expiresAt || null);
        } else {
          setError('No se pudo crear la solicitud de registro.');
        }
      } catch (e: any) {
        setError(e.message || 'No se pudo crear la solicitud de registro.');
      } finally {
        setLoading(false);
      }
    })();
  }, [adminId, token]);

  // Cuenta regresiva del TTL (misma fuente: pairing)
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) setError('El enlace expiró. Cierra y vuelve a intentarlo.');
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [expiresAt]);

  const registerDelivery = async (provider: string, target: string | null) => {
    try {
      await api.registerDelivery(enrollment.id, provider, target, token);
    } catch { /* auditoría de transporte — no bloquea la UX */ }
  };

  const sendWhatsApp = async () => {
    if (!phone.trim()) { setError('Escribe el número de WhatsApp del destinatario.'); return; }
    setError(''); setSending(true);
    try {
      const text = encodeURIComponent(`¡Hola! Te invito a registrar este dispositivo para ${adminName}: ${inviteUrl}`);
      window.open(`https://wa.me/${phone.replace(/\D/g, '')}?text=${text}`, '_blank');
      await registerDelivery('whatsapp', phone.trim());
    } finally { setSending(false); }
  };

  const sendEmail = async () => {
    if (!email.trim()) { setError('Escribe el correo del destinatario.'); return; }
    setError(''); setSending(true);
    try {
      const subject = encodeURIComponent(`Registrar dispositivo — ${adminName}`);
      const body = encodeURIComponent(`Hola,\n\nAbre este enlace desde el dispositivo que quieres registrar para ${adminName}:\n\n${inviteUrl}\n\nEl enlace expira en 10 minutos y es de un solo uso.`);
      window.location.href = `mailto:${email.trim()}?subject=${subject}&body=${body}`;
      await registerDelivery('email', email.trim());
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      await registerDelivery('link', null);
    } catch {
      setError('No se pudo copiar. Copia manualmente: ' + inviteUrl);
    }
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Registrar dispositivo</h2>
              <p className="text-sm text-gray-500">Solicitud para {adminName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
          </div>

          {loading && <p className="text-center text-gray-500 py-8">Creando solicitud…</p>}

          {!loading && error && !enrollment && (
            <div className="text-center py-6">
              <p className="text-sm text-red-500 mb-3">{error}</p>
              <button onClick={onClose} className="text-sm text-purple-600 hover:underline">Cerrar</button>
            </div>
          )}

          {!loading && enrollment && (
            <>
              {secondsLeft > 0 && !error && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
                  <span className="text-sm text-purple-700 font-medium">El enlace expira en</span>
                  <span className="text-lg font-bold text-purple-700 tabular-nums">{mins}:{secs}</span>
                </div>
              )}
              {error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                  <p className="text-sm text-amber-700">{error}</p>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-400 mb-1">Enlace de registro (un solo uso)</p>
                <p className="text-sm text-gray-700 break-words font-mono">{inviteUrl}</p>
              </div>

              <p className="text-sm text-gray-600 mb-3">Envía el enlace al dispositivo que se registrará:</p>

              <div className="space-y-2 mb-4">
                <div className="flex gap-2">
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+52 555 123 4567"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                  <button
                    onClick={sendWhatsApp}
                    disabled={sending}
                    className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap"
                  >
                    WhatsApp
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="beatriz@ejemplo.com"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <button
                    onClick={sendEmail}
                    disabled={sending}
                    className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap"
                  >
                    Email
                  </button>
                </div>
              </div>

              <button
                onClick={copyLink}
                className="w-full border border-gray-300 hover:bg-gray-50 text-sm font-medium text-gray-700 py-2.5 rounded-lg transition-colors"
              >
                {copied ? '✓ Enlace copiado' : '🔗 Copiar enlace'}
              </button>

              <p className="text-xs text-gray-400 mt-4">
                Quien abra el enlace desde su dispositivo quedará autorizado. El enlace caduca en 10
                minutos y solo puede usarse una vez.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
